import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Stack,
  Grid,
  Chip,
  Alert,
  Card,
  CardContent,
  CardHeader,
  Divider,
  TextField,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  InputAdornment,
  CircularProgress,
  Fade,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate, useParams } from "react-router-dom";
import ForbiddenDisplay from "../RedirectPages/AccessDeniedPage.js";
import AutocompleteUsers from "../../components/AutocompleteUsers.js";
import API from "../../api/axiosConfig";
import { useToast } from "../../context/ToastContext";
import { useUser } from "../../context/UserContext";
import { DeleteConfirmationDialog } from "../../components/DeleteConfirmationDialog.js";

/*
    MeetingDetailPage Component
    Fetches and displays specific meeting details, user info, and admin status.
    Coordinates multiple API calls to ensure all data (including reimbursement receipts)
    is ready before the page is fully interactive.
 */
export default function MeetingDetailPage() {
  const showToast = useToast();
  const { id } = useParams();
  const navigate = useNavigate();

  // --- State Management ---
  const [loading, setLoading] = useState(true);
  const [meeting, setMeeting] = useState(null);
  const [reimbursementData, setReimbursementData] = useState(null);
  const [imageSrc, setImageSrc] = useState(null); // Actual image blob URL for rendering
  const { isAdmin, userEmail } = useUser();
  const [viewPermission, setViewPermission] = useState(false); // does the user have permission to view this meeting
  const [error, setError] = useState(null);
  const [newGuestEmail, setNewGuestEmail] = useState(""); // variable for adding a new guest
  const [newGuestFirstName, setNewGuestFirstName] = useState("");
  const [newGuestLastName, setNewGuestLastName] = useState("");
  const [buttonLoading, setButtonLoading] = useState(false); // to deactivate add guest button
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false); // opens the delete dialog box
  const [addReimbursement, setAddReimbursement] = useState(false); // is the add reimbursement window open
  const [amountSpent, setAmountSpent] = useState(null);
  const [isEditingPayee, setIsEditingPayee] = useState(false);
  const [selectedPayee, setSelectedPayee] = useState(null);
  const [payeeLoading, setPayeeLoading] = useState(false);

  // Format for US currency
  const formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  });

  // The selected file to upload for a reimbursement image
  const [selectedFile, setSelectedFile] = useState(null);

  const handleAccept = async (meetingId) => {
    try {
      const response = await API.post("/api/meetings/accept", meetingId, {
        headers: { "Content-Type": "application/json" },
      });
      setMeeting(response.data);
      showToast("Meeting accepted!", "success");
    } catch (err) {
      // Bad request status.
      if (err.response?.status === 400) {
        showToast(
          "Error accepting meeting! User is NOT on pending guest list.",
          "error",
        );
        return;
      } else {
        showToast("Error accepting meeting!", "error");
      }
    }
  };

  const handleDecline = async (meetingId) => {
    try {
      const response = await API.post("/api/meetings/decline", meetingId, {
        headers: { "Content-Type": "application/json" },
      });
      showToast("Meeting declined.", "success");
      setMeeting(response.data);
    } catch (err) {
      // Bad request status.
      if (err.response?.status === 400) {
        showToast(
          "Error declining meeting! User is not on the pending guest list!",
          "error",
        );
      } else {
        // Again, why no navigate or return statement here?
        showToast("Error declining meeting!");
      }
    }
  };

  const loadPageData = useCallback(async () => {
    // Helper: Fetch the binary image data for a receipt
    const fetchImage = async (reimbursementId) => {
      try {
        // Backend uses @GetMapping("/image/{id}"), so we use .get with backticks
        // responseType: 'blob' is required to handle the 'ResponseEntity<Resource>'
        const response = await API.get(
          `/api/reimbursements/image/${reimbursementId}`,
          {
            responseType: "blob",
          },
        );

        // Create the temporary URL for the browser to display
        const objectUrl = URL.createObjectURL(response.data);
        setImageSrc(objectUrl);
      } catch (error) {
        // if it's 404 we will assume the image was never uploaded
        console.error("ERROR FETCHING " + error);
        if (error?.response?.status !== 404) {
          showToast("Error fetching reimbursement image!", "error");
        }
      }
    };

    setLoading(true);
    setError(null);
    try {
      // Initial parallel fetches for Meeting, User, and Admin context
      const meetingRes = await API.post("/api/meetings/get", id, {
        headers: { "Content-Type": "application/json" },
      });

      const currentMeeting = meetingRes.data;
      setMeeting(currentMeeting);
      setViewPermission(true); // rely on backend to do permission checks
      if (currentMeeting.isReimbursement) {
        setReimbursementData(currentMeeting.reimbursement);
        await fetchImage(currentMeeting.reimbursement.id);
      }
    } catch (error) {
      if (error.response?.status === 403) {
        // forbidden
        setViewPermission(false);
        return; // don't set error. this is a permission issue not an error
      }
      console.log("ERROR! " + error);
      setError("Error loading meeting!\n" + error.response?.data?.message);
    } finally {
      setLoading(false);
    }
  }, [id, showToast]);

  const handleSavePayee = async () => {
    if (!selectedPayee || !reimbursementData?.id) return;

    setPayeeLoading(true);
    try {
      if (reimbursementData.status !== "PENDING") {
        // if the reimbursement isn't pending don't let the payee be edited
        // completed and denied reimbursements should not be changed
        showToast(
          "Unable to edit reimbursements that have already been approved/denied",
        );
        return;
      }
      // Adjust endpoint/payload to match your backend DTO
      const payeeEmail =
        typeof selectedPayee === "string" ? selectedPayee : selectedPayee.email;

      const response = await API.post(`/api/reimbursements/changeRecipient`, {
        paidToEmail: payeeEmail,
        reimbursementId: reimbursementData.id,
        parentEmail: reimbursementData.parentEmail,
      });

      setReimbursementData((prev) => ({
        ...prev,
        paidToName: response.data.name,
        paidToEmail: response.data.email,
      }));

      showToast("Payee updated successfully!", "success");
      setIsEditingPayee(false);
    } catch (err) {
      showToast(
        err.response?.data?.message || "Failed to update payee",
        "error",
      );
    } finally {
      setPayeeLoading(false);
    }
  };

  const handleDelete = async () => {
    setButtonLoading(true);
    try {
      await API.post(`/api/meetings/delete`, meeting.id, {
        headers: { "Content-Type": "application/json" },
      });
      navigate("/meetings"); // go back to meetings display
    } catch (error) {
      if (error?.response?.status === 404) {
        // if meeting not found it's already deleted
        navigate("/meetings");
      } else {
        showToast(
          "Error deleting meeting: " + error.response.data.message,
          "error",
        );
      }
    } finally {
      // in case error occurs and they stay on page
      setButtonLoading(false);
    }
  };

  const handleAddGuest = async () => {
    setButtonLoading(true);
    if (!newGuestEmail || !newGuestFirstName || !newGuestLastName) {
      showToast("New guests must have name AND email", "warning");
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newGuestEmail)) {
      showToast("Please enter a valid email", "warning");
      return;
    }
    const payload = {
      firstName: newGuestFirstName,
      lastName: newGuestLastName,
      email: newGuestEmail,
      meetingId: meeting.id,
    };
    try {
      const response = await API.post("/api/meetings/addGuest", payload);

      // refresh meeting state
      setMeeting(response.data);

      // Refresh the data to show the new guest in the list
      setNewGuestFirstName("");
      setNewGuestLastName("");
      setNewGuestEmail(""); // Clear the input
      showToast("Guest added successfully!", "success");
    } catch (err) {
      showToast("Error adding guest", "error");
    } finally {
      setButtonLoading(false);
    }
  };

  // Handle File Selection
  const handleFileChange = (event) => {
    if (event.target.files && event.target.files[0]) {
      setSelectedFile(event.target.files[0]);
    }
  };

  // Cleanup object URLs to prevent memory leaks when component unmounts or image changes
  useEffect(() => {
    return () => {
      if (imageSrc) {
        URL.revokeObjectURL(imageSrc);
      }
    };
  }, [imageSrc]);

  useEffect(() => {
    loadPageData();
  }, [loadPageData]);

  const handleUpload = async (e) => {
    if (!selectedFile) {
      alert("ERROR: Please provide a receipt image!");
      return;
    }

    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("amount", amountSpent);
    formData.append("meetingId", meeting.id);
    formData.append("paidToSelf", true); // assume the reimbursement is paid to the parent of meeting
    formData.append(
      "reason",
      "Reimbursement affiliated with Meeting " + meeting.id,
    );

    try {
      await API.post("/api/reimbursements/upload", formData);

      setSelectedFile(null);
      showToast("Reimbursement Successfully Uploaded!", "success");
      loadPageData();
    } catch (err) {
      showToast("Reimbursement upload failed", "error");
    } finally {
      setLoading(false);
    }
  };

  // attaches receipt to existing reimbursement that didn't have a receipt
  const handleAttachReceipt = async () => {
    if (!selectedFile || !reimbursementData?.id) return;

    setLoading(true);
    const formData = new FormData();
    formData.append("receipt", selectedFile);

    try {
      await API.post(
        `/api/reimbursements/addReceipt/${reimbursementData.id}`,
        formData,
      );

      showToast("Receipt uploaded successfully!", "success");

      // Clear file selection and trigger a re-fetch of the image/meeting
      setSelectedFile(null);
      loadPageData();
    } catch (err) {
      showToast(
        err.response?.data?.message || "Failed to attach receipt.",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  if (!viewPermission) {
    return <ForbiddenDisplay />;
  }

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "80vh",
        }}
      >
        <CircularProgress size={60} />
        <Typography sx={{ mt: 2 }}>Loading meeting context...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box margin={4}>
        <Alert
          severity="error"
          sx={{
            justifyContent: "center",
            textAlign: "center",
          }}
        >
          {error}
        </Alert>
        <Button
          variant="outlined"
          component="span"
          color="primary"
          onClick={loadPageData}
          sx={{ mt: 2 }}
        >
          Retry
        </Button>
      </Box>
    );
  }

  // formatting the meeting.date to be split into a date and a time
  const event = new Date(meeting.time);
  const date = event.toLocaleDateString();
  const time = event.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: "true",
  });

  const totalGuests =
    (meeting.confirmed?.length || 0) +
    (meeting.pending?.length || 0) +
    (meeting.external?.length || 0);

  const editPermission = Boolean(
    meeting?.parent?.email === userEmail || isAdmin,
  );
  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", p: { xs: 2, md: 4 } }}>
      {/* TOP ACTION HEADER */}
      <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={2}
        >
          <Box>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1.5}
              flexWrap="wrap"
            >
              <Typography variant="h4" fontWeight="bold">
                Meeting Details
              </Typography>
              {isAdmin && (
                <Chip
                  label="Admin View"
                  size="small"
                  color="secondary"
                  sx={{ fontWeight: "bold" }}
                />
              )}
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Logged by <strong>{meeting.parent?.name}</strong> (
              {meeting.parent?.email})
            </Typography>
          </Box>

          <Stack direction="row" spacing={1.5} alignItems="center">
            {meeting.isGuest && meeting.isPendingGuest && (
              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  color="success"
                  size="small"
                  onClick={() => handleAccept(meeting.id)}
                >
                  Accept
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={() => handleDecline(meeting.id)}
                >
                  Decline
                </Button>
              </Stack>
            )}

            {editPermission && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => setDeleteDialogOpen(true)}
                disabled={buttonLoading}
                size="small"
              >
                {buttonLoading ? "Processing..." : "Delete Meeting"}
              </Button>
            )}
          </Stack>
        </Stack>
      </Paper>

      {/* 2-COLUMN DASHBOARD GRID */}
      <Grid container spacing={3}>
        {/* ================= LEFT COLUMN: DETAILS & EXPENSES ================= */}
        <Grid item xs={12} md={7}>
          <Stack spacing={3}>
            {/* Core Info Card */}
            <Card elevation={2} sx={{ borderRadius: 2 }}>
              <CardHeader
                title="Overview"
                titleTypographyProps={{ variant: "h6", fontWeight: "bold" }}
              />
              <Divider />
              <CardContent
                sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}
              >
                {/* Location and Meeting Parent */}
                <Grid container spacing={2.5}>
                  {/* Row 1: Location & Host */}
                  <Grid item xs={12} sm={6}>
                    <Stack
                      direction="row"
                      spacing={2}
                      alignItems="flex-start"
                      sx={{ textAlign: "left" }}
                    >
                      <LocationOnOutlinedIcon color="primary" />
                      <Box>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          fontWeight="bold"
                        >
                          LOCATION
                        </Typography>
                        <Typography variant="body1">
                          {meeting.location || "N/A"}
                        </Typography>
                      </Box>
                    </Stack>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Stack
                      direction="row"
                      spacing={2}
                      alignItems="flex-start"
                      sx={{ textAlign: "left" }}
                    >
                      <PersonOutlineOutlinedIcon color="primary" />
                      <Box>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          fontWeight="bold"
                        >
                          ORGANIZER
                        </Typography>
                        <Typography variant="body1">
                          {meeting.parent?.name || "N/A"}
                        </Typography>
                      </Box>
                    </Stack>
                  </Grid>
                </Grid>

                {/* Date & Time */}
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Stack
                      direction="row"
                      spacing={2}
                      alignItems="flex-start"
                      sx={{ textAlign: "left" }}
                    >
                      <CalendarTodayOutlinedIcon color="primary" />
                      <Box>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          fontWeight="bold"
                        >
                          DATE
                        </Typography>
                        <Typography variant="body1">{date || "N/A"}</Typography>
                      </Box>
                    </Stack>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Stack
                      direction="row"
                      spacing={2}
                      alignItems="flex-start"
                      sx={{ textAlign: "left" }}
                    >
                      <AccessTimeOutlinedIcon color="primary" />
                      <Box>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          fontWeight="bold"
                        >
                          TIME
                        </Typography>
                        <Typography variant="body1">{time || "N/A"}</Typography>
                      </Box>
                    </Stack>
                  </Grid>
                </Grid>

                {/* Description */}
                <Box sx={{ mt: 1 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight="bold"
                  >
                    DESCRIPTION
                  </Typography>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      mt: 0.5,
                      borderRadius: 1.5,
                      bgcolor: "action.hover",
                      whiteSpace: "pre-line",
                    }}
                  >
                    <Typography variant="body2">
                      {meeting.description || "No description provided."}
                    </Typography>
                  </Paper>
                </Box>
              </CardContent>
            </Card>

            {/* Reimbursement Card */}
            {reimbursementData ? (
              <Fade in={true} timeout={400}>
                <Card elevation={2} sx={{ borderRadius: 2 }}>
                  <CardHeader
                    avatar={<ReceiptLongOutlinedIcon color="primary" />}
                    title="Reimbursement Details"
                    titleTypographyProps={{ variant: "h6", fontWeight: "bold" }}
                  />
                  <Divider />
                  <CardContent>
                    <Grid container spacing={3} alignItems="center">
                      <Grid item xs={6}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          fontWeight="bold"
                        >
                          REQUESTED AMOUNT
                        </Typography>
                        <Typography
                          variant="h5"
                          color="text.secondary="
                          fontWeight="bold"
                        >
                          {formatter.format(reimbursementData.amount)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography
                          variant="caption"
                          color="text.primary"
                          fontWeight="bold"
                        >
                          CLAIM STATUS
                        </Typography>
                        <Box sx={{ mt: 0.5 }}>
                          <Chip
                            label={reimbursementData.status || "Pending"}
                            color={
                              reimbursementData.status === "Approved"
                                ? "success"
                                : reimbursementData.status === "Rejected"
                                  ? "error"
                                  : "warning"
                            }
                            size="small"
                          />
                        </Box>
                      </Grid>

                      <Grid item xs={12}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          fontWeight="bold"
                        >
                          PAYEE
                        </Typography>

                        {isEditingPayee ? (
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="flex-start"
                            sx={{ mt: 1 }}
                          >
                            <Box sx={{ flexGrow: 1 }}>
                              <AutocompleteUsers
                                size="small"
                                label="Select New Payee"
                                value={selectedPayee}
                                valueKey="email"
                                returnFullObject // Gives you full user object (name + email)
                                onChange={(user) => setSelectedPayee(user)}
                              />
                            </Box>

                            <Button
                              variant="contained"
                              size="small"
                              color="success"
                              disabled={payeeLoading || !selectedPayee}
                              onClick={handleSavePayee}
                              sx={{ height: 40 }}
                            >
                              {payeeLoading ? (
                                <CircularProgress size={18} color="inherit" />
                              ) : (
                                <CheckIcon />
                              )}
                            </Button>

                            <Button
                              variant="outlined"
                              size="small"
                              color="inherit"
                              disabled={payeeLoading}
                              onClick={() => setIsEditingPayee(false)}
                              sx={{ height: 40 }}
                            >
                              <CloseIcon />
                            </Button>
                          </Stack>
                        ) : (
                          <Typography
                            variant="h6"
                            fontWeight="bold"
                            color="text.primary"
                          >
                            {reimbursementData.paidToName ||
                              "No payee assigned"}
                          </Typography>
                        )}

                        {editPermission && !isEditingPayee && (
                          <Button
                            size="small"
                            startIcon={<EditIcon sx={{ fontSize: 16 }} />}
                            onClick={() => {
                              setSelectedPayee(
                                reimbursementData.paidToEmail ||
                                  reimbursementData.paidToName,
                              );
                              setIsEditingPayee(true);
                            }}
                            sx={{
                              minWidth: "auto",
                              p: 0.2,
                              fontSize: "0.75rem",
                            }}
                          >
                            Select a different payee
                          </Button>
                        )}
                      </Grid>

                      <Grid item xs={12}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          fontWeight="bold"
                        >
                          RECEIPT ATTACHMENT
                        </Typography>

                        {imageSrc ? (
                          <Box
                            component="img"
                            src={imageSrc}
                            alt="Receipt"
                            sx={{
                              width: "100%",
                              maxHeight: 350,
                              objectFit: "contain",
                              borderRadius: 1.5,
                              border: "1px solid",
                              borderColor: "divider",
                              mt: 1,
                            }}
                          />
                        ) : editPermission ? (
                          /* Upload drop-zone when receipt is missing & user has edit rights */
                          <Paper
                            variant="outlined"
                            sx={{
                              p: 3,
                              mt: 1,
                              textAlign: "center",
                              bgcolor: "action.hover",
                              borderStyle: "dashed",
                              borderRadius: 2,
                            }}
                          >
                            <Stack spacing={1.5} alignItems="center">
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                No receipt image attached yet.
                              </Typography>

                              <input
                                accept="image/*"
                                style={{ display: "none" }}
                                id="attach-missing-receipt-upload"
                                type="file"
                                onChange={handleFileChange}
                              />

                              <label htmlFor="attach-missing-receipt-upload">
                                <Button
                                  variant="outlined"
                                  component="span"
                                  size="small"
                                  startIcon={<CloudUploadIcon />}
                                >
                                  Choose Receipt Image
                                </Button>
                              </label>

                              {selectedFile && (
                                <Stack
                                  spacing={1}
                                  alignItems="center"
                                  sx={{ mt: 1 }}
                                >
                                  <Typography
                                    variant="caption"
                                    fontWeight="medium"
                                  >
                                    Selected: {selectedFile.name}
                                  </Typography>
                                  <Button
                                    variant="contained"
                                    size="small"
                                    onClick={handleAttachReceipt}
                                    disabled={loading}
                                    startIcon={
                                      loading ? (
                                        <CircularProgress size={16} />
                                      ) : null
                                    }
                                  >
                                    {loading
                                      ? "Uploading..."
                                      : "Upload Receipt"}
                                  </Button>
                                </Stack>
                              )}
                            </Stack>
                          </Paper>
                        ) : (
                          /* Read-only fallback for users without edit rights */
                          <Paper
                            variant="outlined"
                            sx={{
                              p: 2,
                              mt: 1,
                              textAlign: "center",
                              bgcolor: "action.hover",
                            }}
                          >
                            <Typography variant="body2" color="text.secondary">
                              No receipt image available.
                            </Typography>
                          </Paper>
                        )}
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Fade>
            ) : addReimbursement && editPermission ? (
              <Card elevation={2} sx={{ borderRadius: 2 }}>
                <CardHeader
                  avatar={<ReceiptLongOutlinedIcon color="primary" />}
                  title="Submit Reimbursement Claim"
                  titleTypographyProps={{ variant: "h6", fontWeight: "bold" }}
                />
                <Divider />
                <CardContent>
                  <Stack spacing={2.5}>
                    <TextField
                      label="Amount Spent"
                      variant="outlined"
                      size="small"
                      placeholder="0.00"
                      value={amountSpent}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === "" || /^\d*\.?\d{0,2}$/.test(val)) {
                          setAmountSpent(val);
                        }
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">$</InputAdornment>
                        ),
                      }}
                      sx={{ maxWidth: 220 }}
                    />

                    <Box
                      sx={{
                        border: "2px dashed",
                        borderColor: "divider",
                        p: 3,
                        textAlign: "center",
                        borderRadius: 2,
                        bgcolor: "action.hover",
                      }}
                    >
                      <input
                        accept="image/*"
                        style={{ display: "none" }}
                        id="receipt-file-upload"
                        type="file"
                        onChange={handleFileChange}
                      />
                      <label htmlFor="receipt-file-upload">
                        <Button
                          variant="outlined"
                          component="span"
                          startIcon={<CloudUploadIcon />}
                          size="small"
                        >
                          Choose Receipt Image
                        </Button>
                      </label>
                      {selectedFile && (
                        <Typography
                          variant="body2"
                          sx={{ mt: 1 }}
                          fontWeight="medium"
                        >
                          Selected: {selectedFile.name}
                        </Typography>
                      )}
                    </Box>

                    <Stack
                      direction="row"
                      spacing={1.5}
                      justifyContent="flex-end"
                    >
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setAddReimbursement(false)}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        size="small"
                        disabled={!selectedFile || amountSpent <= 0 || loading}
                        startIcon={
                          loading ? <CircularProgress size={16} /> : null
                        }
                        onClick={handleUpload}
                      >
                        {loading ? "Submitting..." : "Submit Claim"}
                      </Button>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            ) : (
              editPermission && (
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<ReceiptLongOutlinedIcon />}
                  onClick={() => setAddReimbursement(true)}
                  sx={{ py: 1.5, borderStyle: "dashed" }}
                  fullWidth
                >
                  Add Reimbursement Request
                </Button>
              )
            )}
          </Stack>
        </Grid>

        {/* ================= RIGHT COLUMN: GUEST LIST & INVITATIONS ================= */}
        <Grid item xs={12} md={5}>
          <Stack spacing={3}>
            <Card elevation={2} sx={{ borderRadius: 2 }}>
              <CardHeader
                avatar={<PeopleAltOutlinedIcon color="primary" />}
                title={`Guest List (${totalGuests})`}
                titleTypographyProps={{ variant: "h6", fontWeight: "bold" }}
              />
              <Divider />
              <CardContent sx={{ p: 0 }}>
                {totalGuests === 0 ? (
                  <Box sx={{ p: 4, textAlign: "center" }}>
                    <Typography variant="body2" color="text.secondary">
                      No guests have been added yet.
                    </Typography>
                  </Box>
                ) : (
                  <List disablePadding>
                    {/* Confirmed Guests */}
                    {meeting.confirmed?.map((guest) => (
                      <ListItem
                        key={guest.email}
                        divider
                        secondaryAction={
                          <Chip
                            label="Confirmed"
                            color="success"
                            size="small"
                            icon={<CheckCircleOutlineIcon />}
                          />
                        }
                      >
                        <ListItemAvatar>
                          <Avatar
                            sx={{
                              bgcolor: "success.main",
                              width: 32,
                              height: 32,
                              fontSize: "0.85rem",
                            }}
                          >
                            {guest.name?.charAt(0) || "U"}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={guest.name}
                          secondary={guest.email}
                          primaryTypographyProps={{ fontWeight: 500 }}
                          secondaryTypographyProps={{
                            variant: "caption",
                            noWrap: true,
                          }}
                        />
                      </ListItem>
                    ))}

                    {/* Pending Guests */}
                    {meeting.pending?.map((guest) => (
                      <ListItem
                        key={guest.email}
                        divider
                        secondaryAction={
                          <Chip
                            label="Pending"
                            color="warning"
                            size="small"
                            variant="outlined"
                            icon={<HourglassEmptyIcon />}
                          />
                        }
                      >
                        <ListItemAvatar>
                          <Avatar
                            sx={{
                              bgcolor: "warning.main",
                              width: 32,
                              height: 32,
                              fontSize: "0.85rem",
                            }}
                          >
                            {guest.name?.charAt(0) || "U"}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={guest.name}
                          secondary={guest.email}
                          primaryTypographyProps={{ fontWeight: 500 }}
                          secondaryTypographyProps={{
                            variant: "caption",
                            noWrap: true,
                          }}
                        />
                      </ListItem>
                    ))}

                    {/* External Guests */}
                    {meeting.external?.map((guest) => {
                      const guestName = guest.name || guest;
                      return (
                        <ListItem
                          key={guest.email || guest}
                          divider
                          secondaryAction={
                            <Chip
                              label="External"
                              size="small"
                              variant="outlined"
                            />
                          }
                        >
                          <ListItemAvatar>
                            <Avatar
                              sx={{
                                width: 32,
                                height: 32,
                                fontSize: "0.85rem",
                              }}
                            >
                              {guestName.charAt(0) || "E"}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={guestName}
                            secondary={guest.email || "No email listed"}
                            primaryTypographyProps={{ fontStyle: "italic" }}
                            secondaryTypographyProps={{ variant: "caption" }}
                          />
                        </ListItem>
                      );
                    })}
                  </List>
                )}
              </CardContent>

              {/* Add Guest Section (Form) */}
              {editPermission && (
                <Box
                  sx={{
                    p: 2.5,
                    bgcolor: "action.hover",
                    borderTop: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    fontWeight="bold"
                    sx={{ mb: 1.5 }}
                  >
                    Invite Another Guest
                  </Typography>
                  <Grid container spacing={1.5}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="First Name"
                        value={newGuestFirstName}
                        onChange={(e) => setNewGuestFirstName(e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Last Name"
                        value={newGuestLastName}
                        onChange={(e) => setNewGuestLastName(e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        size="small"
                        type="email"
                        label="Guest Email"
                        value={newGuestEmail}
                        onChange={(e) => setNewGuestEmail(e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        fullWidth
                        variant="contained"
                        size="small"
                        startIcon={<PersonAddOutlinedIcon />}
                        disabled={buttonLoading || !newGuestEmail}
                        onClick={handleAddGuest}
                        sx={{ mt: 0.5 }}
                      >
                        {buttonLoading ? "Adding..." : "Add Guest"}
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </Card>
          </Stack>
        </Grid>
      </Grid>

      {/* Dialog */}
      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDelete}
        loading={buttonLoading}
        objectToDelete="meeting"
      />
    </Box>
  );
}
